import loader from 'little-loader';
import Promise from 'bluebird';

const DIMENSION_CLIENT_ID           = 'dimension1';
const DIMENSION_HIT_TIME            = 'dimension2';
const DIMENSION_HIT_TYPE            = 'dimension3';
const DIMENSION_VERSION             = 'dimension4';

const DIMENSION_NEW_CATEGORY        = 'dimension5';
const DIMENSION_OVERRIDDEN_CATEGORY = 'dimension6';
const DIMENSION_LABEL               = 'dimension7';

const CATEGORY_CLIENT_ERROR = 'Client error';
const CATEGORY_API_ERROR    = 'Api error';
const CATEGORY_SESSION      = 'Session';
const CATEGORY_CALENDAR     = 'Calendar';
const CATEGORY_DASHBOARD    = 'Dashboard';
const CATEGORY_CATEGORY     = 'Category';
const CATEGORY_TRANSACTION  = 'Transaction';
const CATEGORY_PROFILE      = 'Profile';
const CATEGORY_SUBSCRIPTION = 'Subscription';
const CATEGORY_MODAL        = 'Modal';
const CATEGORY_USER         = 'User';

export class GoogleAnalytics {
    constructor(trackingId, referrealExclusionDomains, version) {
        this.trackingId = trackingId;
        this.referrealExclusionDomains = referrealExclusionDomains || [];
        this.version = version;
    }

    getInstance() {
        if (!this.promise) {
            this.promise = new Promise((resolve, reject) => {
                if (!this.trackingId) {
                    return reject(new Error('Google Analytics disabled. No tracking id provided.'));
                }

                loader('https://www.google-analytics.com/analytics.js', (error) => {
                    if (error) {
                        return reject(error);
                    }

                    return resolve(window.ga);
                });
            }).then(this.initialize.bind(this));
        }

        return this.promise;
    }

    initialize(ga) {
        ga('create', this.trackingId, 'auto', {
            allowLinker: true,
            siteSpeedSampleRate: 100
        });

        ga('require', 'linker');
        ga('linker:autoLink', this.referrealExclusionDomains);

        ga((tracker) => {
            tracker.set('transport', 'beacon');

            tracker.set(DIMENSION_CLIENT_ID, tracker.get('clientId'));
            tracker.set(DIMENSION_VERSION, this.version);

            const originalBuildHitTask = tracker.get('buildHitTask');

            tracker.set('buildHitTask', (model) => {
                model.set(DIMENSION_HIT_TIME, `${new Date().getTime()}`, true);
                model.set(DIMENSION_HIT_TYPE, model.get('hitType'), true);

                originalBuildHitTask(model);
            });
        });

        return ga;
    }

    trackEvent(eventCategory, eventAction, eventLabel, eventValue, options) {
        return this.getInstance().then((ga) => {
            ga('send', 'event', eventCategory, eventAction, eventLabel || '', eventValue || 0, options || null);
        }).catch(() => null);
    }

    trackPageView(page) {
        return this.getInstance().then((ga) => {
            ga('send', 'pageview', {
                page
            });
        }).catch(() => null);
    }

    trackClientError(message) {
        return this.trackEvent(CATEGORY_CLIENT_ERROR, 'error', message, null, {
            nonInteraction: true
        });
    }

    trackApiError(status, error, message, method, url) {
        return this.trackEvent(CATEGORY_API_ERROR, `${status}`, `[${error || 'UNKNOWN'}] ${message} @ ${method} ${url}`, null, {
            nonInteraction: true
        });
    }

    trackSessionEnd(reason, interactive) {
        return this.trackEvent(CATEGORY_SESSION, 'logout', reason, null, {
            nonInteraction: !interactive
        });
    }

    trackSignUp() {
        return this.trackEvent(CATEGORY_USER, 'sign-up');
    }

    trackCalendarSpecificMonthSelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Specific');
    }

    trackCalendarPreviousMonthSelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Previous');
    }

    trackCalendarNextMonthSelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Next');
    }

    trackCalendarTodaySelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Today');
    }

    trackCategoryCreate(category, from) {
        return this.trackEvent(CATEGORY_CATEGORY, 'create', `From ${from}`);
    }

    trackCategoryUpdate() {
        return this.trackEvent(CATEGORY_CATEGORY, 'update');
    }

    trackCategoryDelete() {
        return this.trackEvent(CATEGORY_CATEGORY, 'delete');
    }

    trackTransactionTransferCreate(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'create-transfer', `From ${from}`);
    }

    trackTransactionEventCreate(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'create-event', `From ${from}`);
    }

    trackTransactionBalanceUpdateCreate(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'create-balance-update', `From ${from}`);
    }

    trackTransactionDelete(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'delete', `From ${from}`);
    }

    trackTransactionLabelSuggestionAutocomplete() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'label-autocomplete');
    }

    trackTransactionAutocategoryOverride(transaction, overriddenCategory) {
        let note = transaction.note || '';
        let category = transaction.category ? transaction.category.name || '' : '';
        overriddenCategory = overriddenCategory ? overriddenCategory.name || '' : '';

        return this.trackEvent(CATEGORY_TRANSACTION, 'category-override', `${note}: ${overriddenCategory} -> ${category}`, null, {
            [DIMENSION_LABEL]: note,
            [DIMENSION_OVERRIDDEN_CATEGORY]: overriddenCategory,
            [DIMENSION_NEW_CATEGORY]: transaction.category ? transaction.category.name : ''
        });
    }

    trackTransactionMove() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'move');
    }

    trackTransactionSignChange(transaction) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'change-sign', transaction.type);
    }

    trackTransactionTransferCalendarSwitch() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'switch-transfer-calendars');
    }

    trackTransactionTypeChange(type) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'change-type', type);
    }

    trackTransactionConfirmChange(confirmed) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'change-confirm', confirmed ? 'Confirm' : 'Unconfirm');
    }

    trackTransactionSearch() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'search');
    }

    trackProfilePasswordChange() {
        return this.trackEvent(CATEGORY_PROFILE, 'change-password');
    }

    trackDashboardSelectIncome() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'select-type', 'Income');
    }

    trackDashboardSelectExpense() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'select-type', 'Expense');
    }

    trackDashboardToggleCategoryGroup() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'click-category-group');
    }

    trackDashboardClickEventGroup() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'click-event-group');
    }

    trackPlanSubscribeFromSidebar() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'subscribe', 'From sidebar');
    }

    trackPlanRenewFromSidebar() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'renew', 'From sidebar');
    }

    trackRenewNotificationRenew() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'renew', 'From notification');
    }

    trackRenewNotificationSelectDefaultCalendar() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'select-default-calendar', 'From notification');
    }

    trackModalDismissByBackdrop() {
        return this.trackEvent(CATEGORY_MODAL, 'dismiss', 'Backdrop');
    }

    trackModalDismissByShortcut() {
        return this.trackEvent(CATEGORY_MODAL, 'dismiss', 'Shortcut');
    }

    trackModalDismissByTrigger() {
        return this.trackEvent(CATEGORY_MODAL, 'dismiss', 'Triggered');
    }
}



// WEBPACK FOOTER //
// ./src/common/analytics/google-analytics.js